import React, { useEffect, useState } from 'react';
import { Alert, Col, Collapse, Divider, Row, Tooltip, Typography } from 'antd';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import { Form, FormItem, Input, ResetButton, Select, SubmitButton, Switch } from 'formik-antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import Yup from '../../../../vendor/yup';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import useModifiedDynamicDataErrors from '../../../../app/hooks/useModifiedDynamicDataErrors';
import mkTemplateDynamicFormData from '../../utils/mkTemplateDynamicFormData';
import mkMainAndOptionalTemplateFields from '../../../Template builder/utils/mkMainAndOptionalTemplateFields';
import isFieldRequired from '../../utils/isFieldRequired';
import modifyDefaultValue from '../../../Template builder/utils/modifyDefaultValue';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import getTemplateOptions from '../../../../lib/getTemplateOptions';
import Spinner from '../../../../components/Spinner';
import DynamicFormDataFields from '../../components/DynamicFormDataFields';
import TargetingSettings from '../../../../components/TargetingSettings';
import { makeValidationSchema } from './FormCreateCategory.const';
import { fieldTypeToValidationRule, TYPE_IMAGE, TYPE_OBJECT } from '../../../Template builder/const/templates';
import { CATEGORY_TEMPLATE_CONTENT_TYPE } from '../../pages/Categories/PageCategories/PageCategories.const';
import { useGetTemplatesQuery } from '../../../Template builder/api/templatesApiSlice';



const { Title } = Typography;
const { Panel } = Collapse;

let validation = {};

const FormCreateCategory = ({
  initialErrors,
  initialValues,
  onSubmit,
  isSubmitting,
}) => {
  const intl = useIntl();
  const language = useSelector(({ customise: { language } }) => language);
  const [ currentTemplate, setCurrentTemplate ] = useState(null);

  const { data: templates = { data: [] }, isFetching: isTemplatesFetching } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('status', CATEGORY_TEMPLATE_CONTENT_TYPE),
  });

  const template = templates?.data?.find((template) => Number(template.id) === Number(currentTemplate));
  const modifiedErrors = useModifiedDynamicDataErrors(currentTemplate, template, initialErrors, language);
  const { mainFields, optionalFields } = mkMainAndOptionalTemplateFields(template?.fields?.data);

  useEffect(() => {
    validation = makeValidationSchema({});
  }, []);

  const applyTemplate = async (value, setFieldValue) => {
    setCurrentTemplate(value);
    await setFieldValue('template_id', value);

    if (!value) {
      setFieldValue('data', {});
      return;
    }

    const data = {};
    const dataSchema = {};
    const template = templates?.data?.find((template) => template.id === value);

    template?.fields?.data.filter((field) => field.type !== TYPE_OBJECT)
      .forEach((field) => {
        if (isFieldRequired(field?.validations?.data || [])) {
          dataSchema[field.full_name] = Yup.object().shape({
            template_field_id: Yup.number().required(),
            ...fieldTypeToValidationRule[field.type],
          });
        }

        data[field.full_name] = {
          value: field.type === TYPE_IMAGE ? [] : modifyDefaultValue(field),
          template_field_id: field.id,
        };
      });

    setFieldValue('data', data);
    setFieldValue('target', {
      min_version: template?.target?.data?.min_version,
      max_version: template?.target?.data?.max_version,
      platform: template?.target?.data?.platform,
      countries: template?.target?.data?.countries,
    });

    validation = makeValidationSchema({
      data: Yup.object().shape(dataSchema),
    });
  };

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={modifiedErrors}
      initialValues={initialValues}
      validationSchema={validation}
      onSubmit={async (values, { resetForm }) => {
        onSubmit(mkTemplateDynamicFormData(values, language, template?.fields?.data), resetForm);
      }}
    >
      {({ isValid, dirty, values, setFieldValue, setFieldTouched, resetForm }) => {
        return (
          <Form layout="vertical" >
            <Spinner spinning={isSubmitting}>
              <Title className="hp-mb-24" level={4}>{intl.formatMessage({ id: 'categories-details-title' })}</Title>

              <Row gutter={16}>
                <Col md={12} xs={24}>
                  <Row gutter={16}>
                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-title' })}
                        name='title'
                        required
                      >
                        <Input
                          name="title"
                          placeholder={intl.formatMessage({ id: 'categories-create-form-field-name-placeholder' })}
                        />
                      </FormItem>
                    </Col>
                    <Col span={24}>
                      <FormItem
                        name="client_id"
                        required
                        label={
                          <Row className='w-full' justify="space-between" align="middle">
                            <IntlMessages id="ui-general-app-id" />
                            <Tooltip placement="topRight" title={<IntlMessages id="categories-create-form-field-client-id-help" />}>
                              <InfoCircleOutlined className="hp-ml-8" />
                            </Tooltip>
                          </Row>
                        }
                      >
                        <Input
                          type='number'
                          name='client_id'
                          placeholder={intl.formatMessage({ id: 'ui-general-id-placeholder' })}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-template' })}
                        name='template_id'
                        required
                      >
                        <Select
                          loading={isTemplatesFetching}
                          allowClear
                          showSearch
                          name="template_id"
                          placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                          filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                          options={getTemplateOptions(templates.data)}
                          onChange={async (value) => {
                            await applyTemplate(value, setFieldValue);
                          }}
                        />
                      </FormItem>
                    </Col>

                    <Col span={24}>
                      <FormItem
                        label={intl.formatMessage({ id: 'ui-general-description' })}
                        name='description'
                        required
                      >
                        <Input.TextArea
                          rows={3}
                          maxLength={5000}
                          showCount
                          name="description"
                          type="text"
                          placeholder={intl.formatMessage({ id: 'ui-general-specify-description' })}
                        />
                      </FormItem>
                    </Col>

                    <PrivateComponent allowedPermissions={[ Permissions.CONTENT.CATEGORIES.EDIT_OPTIONS ]}>
                      <Col span={24}>
                        <FormItem
                          name='options'
                          label={
                            <Row className='w-full' justify="space-between" align="middle">
                              <IntlMessages id="categories-form-field-options" />
                              <Tooltip placement="topRight" title={<IntlMessages id="categories-form-field-options-help" />}>
                                <InfoCircleOutlined className="hp-ml-8" />
                              </Tooltip>
                            </Row>
                          }
                        >
                          <Input.TextArea
                            rows={5}
                            showCount
                            maxLength={5000}
                            placeholder={intl.formatMessage({ id: 'categories-form-field-options-placeholder' })}
                            name='options'
                          />
                        </FormItem>
                      </Col>
                    </PrivateComponent>
                  </Row>
                </Col>
              </Row>

              {values?.template_id && (
                <>
                  <Divider />
                  <Title className="hp-mb-24" level={4}>
                    <IntlMessages id="ui-general-template-data" />
                  </Title>
                  <Row gutter={[ 16, 16 ]} className="hp-mb-24">
                    <Col span={24}>
                      <DynamicFormDataFields
                        setFieldValue={setFieldValue}
                        fields={mainFields}
                        values={values}
                        span={24}
                      />
                      {!!optionalFields.length && (
                        <Collapse>
                          <Panel
                            key="1"
                            header={
                              <Title level={5} className='collapse-item-title'>
                                <IntlMessages id="ui-general-additional-parameters" />
                              </Title>
                            }
                          >
                            <DynamicFormDataFields
                              setFieldValue={setFieldValue}
                              fields={optionalFields}
                              values={values}
                              span={24}
                            />
                          </Panel>
                        </Collapse>
                      )}
                    </Col>
                  </Row>

                  <Divider />

                  <TargetingSettings
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    values={values}
                  />
                </>
              )}

              <Alert
                message={<IntlMessages id="ui-general-edit-has-changes" />}
                type="info"
                showIcon
              />

              <Row justify='end' className='hp-mt-24 hp-mb-24'>
                <Col>
                  <Row>
                    <IntlMessages id="ui-general-publish-changes" />
                    <Switch name="is_published" className='hp-ml-16' />
                  </Row>
                </Col>
              </Row>

              <Row gutter={[ 16, 16 ]} className='hp-mt-16' justify='end'>
                <Col>
                  <ResetButton
                    disabled={false}
                    onClick={() => {
                      setCurrentTemplate(null);
                      resetForm(initialValues);
                    }}
                  >
                    <IntlMessages id='ui-general-reset' />
                  </ResetButton>
                </Col>

                <Col>
                  <SubmitButton
                    type="primary"
                    loading={isSubmitting}
                    disabled={!isValid || !dirty}
                  >
                    <IntlMessages id="ui-general-save" />
                  </SubmitButton>
                </Col>
              </Row>
            </Spinner>
          </Form>
        );
      }}
    </Formik>
  );
};

FormCreateCategory.propTypes = {
  initialValues: PropTypes.object.isRequired,
  initialErrors: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};

export default FormCreateCategory;
